import { HubConnectionState, type HubConnection } from '@microsoft/signalr';
import { MANUAL_PING_INTERVAL_MS } from './SignalR.constants';
import { getWebSocketFromConnection } from './SignalR.internals';

export interface ManualPingRefs {
  pingIntervals: Map<string, ReturnType<typeof setInterval>>;
  sockets: Map<string, WebSocket>;
}

export const clearManualPing = (
  hubName: string,
  refs: ManualPingRefs,
): void => {
  const existingInterval = refs.pingIntervals.get(hubName);
  if (existingInterval) {
    clearInterval(existingInterval);
    refs.pingIntervals.delete(hubName);
    console.log(`🛑 [${hubName}] Manual ping interval stopped`);
  }
  refs.sockets.delete(hubName);
};

export const startManualPing = (
  hubName: string,
  connection: HubConnection,
  refs: ManualPingRefs,
): void => {
  clearManualPing(hubName, refs);

  const pingInterval = setInterval(() => {
    if (connection.state !== HubConnectionState.Connected) {
      clearInterval(pingInterval);
      refs.pingIntervals.delete(hubName);
      return;
    }

    try {
      const socket = refs.sockets.get(hubName) ?? getWebSocketFromConnection(connection);

      if (socket instanceof WebSocket) {
        refs.sockets.set(hubName, socket);
      }

      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 6 }));
        console.log(`💓 [${hubName}] Manual ping sent`);
        return;
      }

      console.warn(
        `⚠️ [${hubName}] Cannot send ping - WebSocket not open (readyState: ${socket?.readyState ?? 'n/a'}, expected: ${WebSocket.OPEN})`,
      );
      refs.sockets.delete(hubName);
    } catch (error) {
      console.error(`❌ [${hubName}] Error sending manual ping:`, error);
    }
  }, MANUAL_PING_INTERVAL_MS);

  refs.pingIntervals.set(hubName, pingInterval);
  console.log(`✅ [${hubName}] Manual ping interval started (every 60s)`);
};
